import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { safeRest, uniqueId, getCopy } from '@tds/util-helpers';
import A11yContent from '@tds/core-a11y-content';
import Box from '@tds/core-box';
import { componentWithName } from '@tds/util-prop-types';
import { colorTelusPurple, colorWhite } from '@tds/core-colours';
import { Close, Add } from '@tds/core-interactive-icon';

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends() {
  module.exports = _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

module.exports = _extends;
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

var tagColors = {
  selected: {
    text: colorWhite,
    default: colorTelusPurple,
    hover: '#33204C',
    active: '#201430',
    focus: '#33204C',
    outline: '#7557A0',
    border: '#33204C'
  },
  unselected: {
    text: colorTelusPurple,
    default: colorWhite,
    hover: colorWhite,
    active: '#F1EFF4',
    focus: colorWhite,
    outline: '#7557A0',
    border: '#33204C'
  }
};
var StyledTagButton = styled(Box)({
  marginTop: '1rem' // add vertical spacing when TagItems wrap

}, function (_ref) {
  var isSelected = _ref.isSelected,
      isLoading = _ref.isLoading;
  var colors = tagColors[isSelected ? 'selected' : 'unselected'];
  return {
    overflow: 'visible',
    color: colors.text,
    background: isLoading ? colors.active : colors.default,
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    border: "1px solid ".concat(colorTelusPurple),
    borderRadius: '1.25rem',
    height: '2.5rem',
    '&:hover': {
      background: colors.hover,
      border: isSelected && "1px solid ".concat(tagColors.selected.hover)
    },
    '&:hover:after': {
      content: "''",
      position: 'absolute',
      top: '-2px',
      left: '-2px',
      bottom: '-2px',
      right: '-2px',
      border: "1px solid ".concat(colors.border),
      borderRadius: '1.375rem'
    },
    '::-moz-focus-inner': {
      border: 0
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: isLoading ? colors.active : colors.focus,
      ':after': {
        content: "''",
        position: 'absolute',
        top: isSelected ? '-10px' : '-11px',
        left: isSelected ? '-10px' : '-11px',
        bottom: isSelected ? '-10px' : '-11px',
        right: isSelected ? '-10px' : '-11px',
        border: "".concat(isSelected ? '2px' : '3px', " solid ").concat(colors.outline),
        margin: '5px',
        borderRadius: '1.5625rem'
      }
    },
    '&:active': {
      backgroundColor: colors.active,
      border: isSelected && "1px solid ".concat(colors.active)
    },
    ':after': {
      content: "''"
    }
  };
});
var StyledText = styled.span({
  marginLeft: '0.5rem'
});
var TagItem = forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
      isSelected = _ref2.isSelected,
      isLoading = _ref2.isLoading,
      onClick = _ref2.onClick,
      rest = objectWithoutProperties(_ref2, ["children", "isSelected", "isLoading", "onClick"]);

  var handleClick = function handleClick() {
    onClick(children);
  };

  var Icon = isSelected ? Close : Add;
  return React.createElement(StyledTagButton, _extends_1({}, safeRest(rest), {
    ref: ref,
    between: 2,
    inline: true,
    isSelected: isSelected,
    isLoading: isLoading,
    tag: "button",
    role: "switch",
    "aria-checked": isSelected,
    onClick: handleClick
  }), React.createElement(StyledText, null, children), React.createElement(Icon, {
    color: isSelected ? 'white' : 'telusPurple'
  }));
});
TagItem.propTypes = {
  /**
   * The button text
   */
  children: PropTypes.node.isRequired,

  /**
   * The selected state
   */
  isSelected: PropTypes.bool,

  /**
   * The loading state
   */
  isLoading: PropTypes.bool,

  /**
   * @param {node} name The `children` prop of the `Tags.Item`
   */
  onClick: PropTypes.func
};
TagItem.defaultProps = {
  isSelected: false,
  isLoading: false,
  onClick: undefined
};
TagItem.displayName = 'TagItem';

var warn = function warn(componentName, message) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  console.warn("[TDS] ".concat(componentName, ": ").concat(message)); // eslint-disable-line no-console
};

var copyDictionary = {
  en: {
    a11yLabel: 'Filter products by %{tag}',
    a11yDescriptionSet: 'Products currently filtered by %{tags}',
    a11yDescriptionUnset: 'No product filters currently applied'
  },
  fr: {
    a11yLabel: 'Produits de la catégorie %{tag}',
    a11yDescriptionSet: 'Produits des catégories %{tags}',
    a11yDescriptionUnset: 'Aucun filtre de recherche n’est appliqué'
  }
};

var StyledTagsContainer = styled(Box)({
  flexWrap: 'wrap',
  marginTop: '-1rem' // add negative vertical spacing when TagItems wrap

});
/**
 * @version ./package.json
 */

var Tags = function Tags(_ref) {
  var children = _ref.children,
      tags = _ref.tags,
      copy = _ref.copy,
      onClick = _ref.onClick,
      rest = objectWithoutProperties(_ref, ["children", "tags", "copy", "onClick"]);

  var items;

  if (children === undefined && tags === undefined) {
    warn('Tags', 'At least one of `children` or `tags` props are required');
    return undefined;
  }

  var descriptionBoxId = uniqueId('tags');

  var requiredProps = function requiredProps(tag) {
    return {
      'aria-describedby': descriptionBoxId,
      'aria-label': getCopy(copyDictionary, copy).a11yLabel.replace('%{tag}', tag.children)
    };
  };

  if (children) {
    items = React.Children.map(children, function (child) {
      return React.createElement(child.type, _extends_1({}, child.props, {
        onClick: child.props.onClick || onClick
      }, requiredProps(child.props)));
    });
  } else {
    items = tags.map(function (tag) {
      return React.createElement(TagItem, _extends_1({
        key: tag.children
      }, tag, {
        onClick: tag.onClick || onClick
      }, requiredProps(tag)));
    });
  }

  var selectedTags = items.filter(function (tag) {
    return tag.props.isSelected;
  }).map(function (tag) {
    return tag.props.children;
  });
  return React.createElement("div", safeRest(rest), React.createElement(A11yContent, {
    "aria-live": "assertive",
    id: descriptionBoxId
  }, selectedTags.length > 0 ? getCopy(copyDictionary, copy).a11yDescriptionSet.replace('%{tags}', selectedTags.join(', ')) : getCopy(copyDictionary, copy).a11yDescriptionUnset), React.createElement(StyledTagsContainer, {
    between: 3,
    inline: true
  }, items));
};

Tags.propTypes = {
  /**
   * The `Tags.Item`, can be used as an alternative to the `tags` prop.
   */
  children: componentWithName('TagItem', true),

  /**
   * Use the copy prop to either select provided English or French copy by passing `'en'` or `'fr'` respectively.
   *
   * To provide your own copy, pass an object with the keys `a11yLabel`, `a11yDescriptionSet`, `a11yDescriptionUnset`.
   *
   * See documentation for more details.
   */
  copy: PropTypes.oneOfType([PropTypes.oneOf(['en', 'fr']), PropTypes.shape({
    a11yLabel: PropTypes.string.isRequired,
    a11yDescriptionSet: PropTypes.string.isRequired,
    a11yDescriptionUnset: PropTypes.string.isRequired
  })]).isRequired,

  /**
   * The tags, can be used as an alternative to the `children` prop.
   */
  tags: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    isLoading: PropTypes.bool,
    ref: PropTypes.node
  })),

  /**
   * @param {node} name The `children` prop of the `Tags.Item`.
   */
  onClick: PropTypes.func.isRequired
};
Tags.defaultProps = {
  children: undefined,
  tags: undefined
};
Tags.Item = TagItem;

export default Tags;
